import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
    AthleteAdminBreadcrumbNav,
    AthleteAdminNavState,
    getTeamMemberBreadcrumbLabel,
    navigateToTeamMemberFromBreadcrumb,
    useAdminAthleteDisplayName,
    useAthleteStaffForAdminBreadcrumb,
} from "./AthleteAdminBreadcrumbNav";
import { isAthlete, isGuardian } from "../utils/auth";
import {
    fetchNcaaTrackerByProfileId,
    fetchNcaaTrackerForStaff,
    fetchNcaaTrackerMe,
    NcaaDivisionPayload,
    NcaaTrackerPayload,
    NcaaSection,
    NcaaCourseRow,
    saveNcaaTrackerForStaff,
    saveNcaaTrackerMe,
} from "../services/ncaaTrackerApi";
import { gradeToPoints, parseCredits, rowQualityPoints } from "../utils/ncaaGradeUtils";
import ncaaHelpMark from "../images/ncaa-help-ncaa-mark.svg";
import "../styles/athlete-admin-layout.css";
import "../styles/athlete-admin-scaled-wrapper.css";
import "../styles/ncaa-eligibility-tracker.css";
import "../styles/program-page.css";
import "../styles/athlete-admin-page-spacing.css";

interface NcaaSectionDef {
    id: string;
    title: string;
    requirement: string;
    /** Short label for TOTAL ___ UNITS in section footer (defaults to `title`) */
    footerUnitsName?: string;
}

const SECTION_DEFS_D1: NcaaSectionDef[] = [
    { id: "english", title: "English", requirement: "4 Years Required" },
    { id: "math", title: "Math", requirement: "3 Years Required" },
    { id: "science", title: "Science", requirement: "2 Years Required" },
    {
        id: "extra",
        title: "Extra (English, Math, or Science)",
        requirement: "1 Year Required",
        footerUnitsName: "EXTRA",
    },
    { id: "social", title: "Social Science", requirement: "2 Years Required" },
    { id: "other", title: "Other academic courses", requirement: "4 Years Required" },
];

const SECTION_DEFS_D2: NcaaSectionDef[] = [
    { id: "english", title: "English", requirement: "3 Years Required" },
    { id: "math", title: "Math", requirement: "2 Years Required" },
    { id: "science", title: "Science", requirement: "2 Years Required" },
    {
        id: "extra",
        title: "Extra (EXTRA YEARS IN ENGLISH, MATH OR SCIENCE (3 YEARS REQUIRED))",
        requirement: "",
        footerUnitsName: "EXTRA",
    },
    { id: "social", title: "Social Science", requirement: "2 Years Required" },
    { id: "other", title: "Other academic courses", requirement: "4 Years Required" },
];

function footerUnitsUpper(def: NcaaSectionDef): string {
    return (def.footerUnitsName ?? def.title).toUpperCase();
}

type TabKey = "d1" | "d2";

/** Eligibility Center / course list — official hub for approved courses */
const NCAA_ELIGIBILITY_CENTER_URL =
    "https://web3.ncaa.org/hsportal/exec/hsAction?hsActionSubmit=searchHighS";

/** Eligibility Center account registration */
const NCAA_ELIGIBILITY_CENTER_CREATE_ACCOUNT = "https://web3.ncaa.org/ecwr3/";

const NCAA_DISCLAIMER = {
    heading: "Important Disclaimer",
    body: "The information provided through this worksheet and related materials is intended for general informational purposes only. Due to the manual nature of data entry and course tracking, Polaris Athlete LLC does not guarantee the accuracy or completeness of this information. Any reliance on this material is at your own risk. Polaris Athlete LLC disclaims all liability and responsibility for any decisions or outcomes resulting from the use of this worksheet or its contents by any individual or entity.",
};

const WORKSHEET_INTRO = {
    paragraph:
        "This worksheet is provided to assist you in monitoring your progress toward meeting NCAA initial-eligibility standards. The NCAA Eligibility Center will determine your academic status after you graduate.",
    scaleBold:
        "Use the following scale for grade entry: A = 4; B = 3; C = 2; D = 1. Quality points are calculated as credit multiplied by grade points.",
};

function normalizeGradeInput(input: string): string | null {
    const normalized = input.trim();
    if (normalized === "") return "";
    if (!/^\d+$/.test(normalized)) return null;
    const value = Number.parseInt(normalized, 10);
    if (value < 1 || value > 4) return null;
    return normalized;
}

function formatFormulaCredit(creditsRaw: string): string | null {
    const parsed = parseCredits(creditsRaw);
    if (parsed == null) return null;
    if (parsed > 0 && parsed < 1) {
        return parsed.toString().replace(/^0\./, ".");
    }
    return parsed.toString();
}

function newRowId(): string {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return `r-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function emptyRow(): NcaaCourseRow {
    return { id: newRowId(), icy: false, title: "", grade: "", credits: "" };
}

function ensureMinRows(rows: NcaaCourseRow[], minRows = 4): NcaaCourseRow[] {
    const normalized = rows.map((r) => ({
        id: typeof r.id === "string" ? r.id : newRowId(),
        icy: Boolean(r.icy),
        title: String(r.title ?? ""),
        grade: String(r.grade ?? ""),
        credits: String(r.credits ?? ""),
    }));
    while (normalized.length < minRows) {
        normalized.push(emptyRow());
    }
    return normalized;
}

function emptyDivision(which: TabKey): NcaaDivisionPayload {
    const defs = which === "d1" ? SECTION_DEFS_D1 : SECTION_DEFS_D2;
    return {
        sections: defs.map((d) => ({
            id: d.id,
            rows: ensureMinRows([]),
        })),
    };
}

function defaultPayload(): NcaaTrackerPayload {
    return { d1: emptyDivision("d1"), d2: emptyDivision("d2") };
}

function mergeDivision(div: unknown, fallback: NcaaDivisionPayload, defs: NcaaSectionDef[]): NcaaDivisionPayload {
    if (!div || typeof div !== "object") {
        return fallback;
    }
    const raw = div as { sections?: unknown };
    if (!Array.isArray(raw.sections)) {
        return fallback;
    }
    const byId = new Map<string, NcaaSection>();
    for (const s of raw.sections) {
        if (s && typeof s === "object" && typeof (s as NcaaSection).id === "string") {
            const sec = s as NcaaSection;
            byId.set(sec.id, sec);
        }
    }
    return {
        sections: defs.map((def) => {
            const existing = byId.get(def.id);
            if (existing?.rows?.length) {
                return {
                    id: def.id,
                    rows: ensureMinRows(existing.rows),
                };
            }
            return fallback.sections.find((s) => s.id === def.id)!;
        }),
    };
}

function normalizePayload(raw: unknown): NcaaTrackerPayload {
    const def = defaultPayload();
    if (!raw || typeof raw !== "object") {
        return def;
    }
    const o = raw as Record<string, unknown>;
    return {
        d1: mergeDivision(o.d1, def.d1, SECTION_DEFS_D1),
        d2: mergeDivision(o.d2, def.d2, SECTION_DEFS_D2),
    };
}

interface CoachData {
    id?: string;
    firstName?: string;
    lastName?: string;
}

export default function NcaaEligibilityTrackerPage() {
    const { athleteId } = useParams<{ athleteId?: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const navState = location.state as AthleteAdminNavState | null;

    const guardianViewingAthleteId = localStorage.getItem("guardianViewingAthleteId");
    const readOnly = isGuardian() && Boolean(guardianViewingAthleteId?.trim());
    const isAdminViewingAthlete = !isAthlete() && !isGuardian() && Boolean(athleteId);
    const useFullWidthNcaaStack = isAdminViewingAthlete || isAthlete();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [savedAt, setSavedAt] = useState<string | null>(null);
    const [tab, setTab] = useState<TabKey>("d1");
    const [helpfulLinksOpen, setHelpfulLinksOpen] = useState(false);
    const [payload, setPayload] = useState<NcaaTrackerPayload>(defaultPayload);
    const [coachData, setCoachData] = useState<CoachData | null>(null);

    const athleteBreadcrumbName = useAdminAthleteDisplayName(athleteId, navState?.athleteDisplayName);
    const { staffId: athleteStaffId, staffName: athleteStaffName } = useAthleteStaffForAdminBreadcrumb(
        athleteId,
        Boolean(isAdminViewingAthlete && athleteId)
    );

    const handleNavigateToTeamMember = () => {
        navigateToTeamMemberFromBreadcrumb(navigate, navState?.teamMemberId, athleteStaffId ?? undefined);
    };

    const adminBreadcrumb = isAdminViewingAthlete ? (
        <AthleteAdminBreadcrumbNav
            pageLabel="NCAA Eligibility Tracker"
            teamMemberLabel={getTeamMemberBreadcrumbLabel(coachData, navState?.teamMemberName, athleteStaffName ?? undefined)}
            onTeamMemberClick={handleNavigateToTeamMember}
            athleteLabel={athleteBreadcrumbName}
            athleteProfileId={athleteId}
        />
    ) : null;

    const loadTracker = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            if (navState?.teamMemberName) {
                const nameParts = navState.teamMemberName.split(" ");
                setCoachData({
                    id: navState.teamMemberId,
                    firstName: nameParts[0] || navState.teamMemberName,
                    lastName: nameParts.slice(1).join(" ") || "",
                });
            }

            let res;
            if (isAdminViewingAthlete && athleteId && /^\d+$/.test(athleteId)) {
                res = await fetchNcaaTrackerForStaff(parseInt(athleteId, 10));
            } else if (readOnly && guardianViewingAthleteId?.trim()) {
                res = await fetchNcaaTrackerByProfileId(parseInt(guardianViewingAthleteId.trim(), 10));
            } else {
                res = await fetchNcaaTrackerMe();
            }
            setPayload(normalizePayload(res.payload));
            setSavedAt(res.updatedAt);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : "Failed to load tracker");
        } finally {
            setLoading(false);
        }
    }, [athleteId, guardianViewingAthleteId, isAdminViewingAthlete, navState?.teamMemberId, navState?.teamMemberName, readOnly]);

    useEffect(() => {
        loadTracker();
    }, [loadTracker]);

    const division = tab === "d1" ? payload.d1 : payload.d2;
    const sectionDefs = tab === "d1" ? SECTION_DEFS_D1 : SECTION_DEFS_D2;

    const totals = useMemo(() => {
        let totalQp = 0;
        let totalCr = 0;
        const allDivisions: NcaaDivisionPayload[] = [payload.d1, payload.d2];
        for (const div of allDivisions) {
            for (const sec of div.sections) {
                for (const row of sec.rows) {
                    totalQp += rowQualityPoints(row.grade, row.credits);
                    const c = parseCredits(row.credits);
                    if (c != null) {
                        totalCr += c;
                    }
                }
            }
        }
        const gpa = totalCr > 0 ? totalQp / totalCr : 0;
        return { totalQp, totalCr, gpa };
    }, [payload.d1, payload.d2]);

    const worksheetTotals = useMemo(() => {
        let totalQp = 0;
        let totalCr = 0;
        for (const sec of division.sections) {
            for (const row of sec.rows) {
                totalQp += rowQualityPoints(row.grade, row.credits);
                const c = parseCredits(row.credits);
                if (c != null) {
                    totalCr += c;
                }
            }
        }
        const gpa = totalCr > 0 ? totalQp / totalCr : 0;
        return { totalQp, totalCr, gpa };
    }, [division]);

    const updateDivision = (next: NcaaDivisionPayload) => {
        setPayload((p) => (tab === "d1" ? { ...p, d1: next } : { ...p, d2: next }));
    };

    const updateSectionRows = (sectionId: string, rows: NcaaCourseRow[]) => {
        const next: NcaaDivisionPayload = {
            sections: division.sections.map((s) => (s.id === sectionId ? { ...s, rows } : s)),
        };
        updateDivision(next);
    };

    const addCourse = (sectionId: string) => {
        const sec = division.sections.find((s) => s.id === sectionId);
        if (!sec) return;
        updateSectionRows(sectionId, [...sec.rows, emptyRow()]);
    };

    const updateRow = (sectionId: string, rowId: string, patch: Partial<NcaaCourseRow>) => {
        const sec = division.sections.find((s) => s.id === sectionId);
        if (!sec) return;
        updateSectionRows(
            sectionId,
            sec.rows.map((r) => (r.id === rowId ? { ...r, ...patch } : r))
        );
    };

    const sectionTotals = (sec: NcaaSection) => {
        let qp = 0;
        let cr = 0;
        for (const row of sec.rows) {
            qp += rowQualityPoints(row.grade, row.credits);
            const c = parseCredits(row.credits);
            if (c != null) {
                cr += c;
            }
        }
        return { qp, cr };
    };

    const initialLoadedRef = useRef(false);
    const inFlightRef = useRef(false);
    const pendingRef = useRef(false);
    const payloadRef = useRef(payload);
    payloadRef.current = payload;

    const runSave = useCallback(async () => {
        if (readOnly || isGuardian()) return;
        if (inFlightRef.current) {
            pendingRef.current = true;
            return;
        }
        inFlightRef.current = true;
        setSaving(true);
        setSaveError(null);
        try {
            const snapshot = payloadRef.current;
            let res;
            if (isAdminViewingAthlete && athleteId && /^\d+$/.test(athleteId)) {
                res = await saveNcaaTrackerForStaff(parseInt(athleteId, 10), snapshot, 1);
            } else {
                res = await saveNcaaTrackerMe(snapshot, 1);
            }
            setSavedAt(res.updatedAt);
        } catch (e: unknown) {
            setSaveError(e instanceof Error ? e.message : "Save failed");
        } finally {
            inFlightRef.current = false;
            setSaving(false);
            if (pendingRef.current) {
                pendingRef.current = false;
                void runSave();
            }
        }
    }, [athleteId, isAdminViewingAthlete, readOnly]);

    useEffect(() => {
        if (loading || readOnly || isGuardian()) return;
        if (!initialLoadedRef.current) {
            initialLoadedRef.current = true;
            return;
        }
        const t = setTimeout(() => {
            void runSave();
        }, 1000);
        return () => clearTimeout(t);
    }, [payload, loading, readOnly, runSave]);

    if (loading) {
        return (
            <div className={`ncaa-tracker-wrapper ${isAdminViewingAthlete ? "athlete-admin-view" : ""}`}>
                {adminBreadcrumb}
                <div className={`ncaa-tracker-stack ${useFullWidthNcaaStack ? "ncaa-tracker-stack--with-sidebar" : ""}`}>
                    <div className="ncaa-tracker-panel">
                        <div className="loading-spinner">Loading...</div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={`ncaa-tracker-wrapper ${isAdminViewingAthlete ? "athlete-admin-view" : ""}`}>
                {adminBreadcrumb}
                <div className={`ncaa-tracker-stack ${useFullWidthNcaaStack ? "ncaa-tracker-stack--with-sidebar" : ""}`}>
                    <div className="ncaa-tracker-panel">
                        <div className="error-message">{error}</div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`ncaa-tracker-wrapper ${isAdminViewingAthlete ? "athlete-admin-view" : ""}`}>
            {adminBreadcrumb}
            <div className={`ncaa-tracker-stack ${useFullWidthNcaaStack ? "ncaa-tracker-stack--with-sidebar" : ""}`}>
                {/* Overall tracking — on page grey in admin (NIL tab rhythm) */}
                <section className="ncaa-tracker-panel ncaa-tracker-panel--tracking" aria-label="Overall tracking">
                    {!useFullWidthNcaaStack && (
                        <h1 className="ncaa-tracker-title">NCAA Eligibility Tracker</h1>
                    )}

                    <div className="ncaa-rail-block">
                        <h2 className="ncaa-rail-heading">Overall tracking</h2>
                        <div className="ncaa-summary-cards">
                            <div className="ncaa-summary-card">
                                <div className="ncaa-summary-icon ncaa-summary-icon--gpa" aria-hidden />
                                <div className="ncaa-summary-text">
                                    <div className="ncaa-summary-label">CORE-COURSE GPA</div>
                                    <div className="ncaa-summary-value">{totals.gpa.toFixed(2)}</div>
                                </div>
                            </div>
                            <div className="ncaa-summary-card">
                                <div className="ncaa-summary-icon ncaa-summary-icon--qp" aria-hidden />
                                <div className="ncaa-summary-text">
                                    <div className="ncaa-summary-label">QUALITY POINTS</div>
                                    <div className="ncaa-summary-value">{totals.totalQp.toFixed(1)}</div>
                                </div>
                            </div>
                            <div className="ncaa-summary-card">
                                <div className="ncaa-summary-icon ncaa-summary-icon--credits" aria-hidden />
                                <div className="ncaa-summary-text">
                                    <div className="ncaa-summary-label">CREDITS</div>
                                    <div className="ncaa-summary-value">{totals.totalCr.toFixed(1)}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Helpful links — collapsible (Program page accordion pattern) */}
                <section
                    className={`ncaa-tracker-panel ncaa-tracker-panel--helpful-links${helpfulLinksOpen ? "" : " ncaa-tracker-panel--helpful-links-collapsed"}`}
                    aria-label="Helpful links"
                >
                    <div className="program-accordion-head">
                        <button
                            type="button"
                            className="program-accordion-toggle"
                            onClick={() => setHelpfulLinksOpen((open) => !open)}
                            aria-expanded={helpfulLinksOpen}
                            aria-controls="ncaa-helpful-links-panel"
                            id="ncaa-helpful-links-trigger"
                        >
                            <span className="program-section-title ncaa-helpful-links-title">Helpful Links</span>
                        </button>
                        <div className="program-section-head-right">
                            <button
                                type="button"
                                className="program-accordion-chevron-btn"
                                onClick={() => setHelpfulLinksOpen((open) => !open)}
                                aria-expanded={helpfulLinksOpen}
                                aria-controls="ncaa-helpful-links-panel"
                                aria-label={
                                    helpfulLinksOpen ? "Collapse helpful links section" : "Expand helpful links section"
                                }
                            >
                                <span className="program-accordion-chevron" aria-hidden>
                                    {helpfulLinksOpen ? "▼" : "▶"}
                                </span>
                            </button>
                        </div>
                    </div>
                    {helpfulLinksOpen && (
                        <div
                            id="ncaa-helpful-links-panel"
                            role="region"
                            aria-labelledby="ncaa-helpful-links-trigger"
                            className="program-accordion-panel"
                        >
                            <div className="ncaa-helpful-links">
                                <div className="ncaa-help-card ncaa-help-card--link-surface ncaa-help-card--centered">
                                <div className="ncaa-help-card-body">
                                    <img src={ncaaHelpMark} alt="" className="ncaa-help-logo-img" width={64} height={64} />
                                    <p className="ncaa-help-card-text">
                                        Remember to check your high school&apos;s list of NCAA-approved core courses for the
                                        classes you have taken or plan to take.
                                    </p>
                                </div>
                                <div className="ncaa-help-card-actions">
                                    <button
                                        type="button"
                                        className="ncaa-pill-btn ncaa-pill-btn--red"
                                        onClick={() =>
                                            window.open(NCAA_ELIGIBILITY_CENTER_URL, "_blank", "noopener,noreferrer")
                                        }
                                    >
                                        Review courses
                                    </button>
                                </div>
                            </div>
                            <div className="ncaa-help-card ncaa-help-card--link-surface ncaa-help-card--centered">
                                <div className="ncaa-help-card-body">
                                    <h3 className="ncaa-help-card-title">Create your NCAA Eligibility Center account</h3>
                                    <p className="ncaa-help-card-text">
                                        Begin your eligibility journey and make sure you&apos;re on track to compete at the
                                        college level.
                                    </p>
                                </div>
                                <div className="ncaa-help-card-actions">
                                    <button
                                        type="button"
                                        className="ncaa-pill-btn ncaa-pill-btn--red"
                                        onClick={() =>
                                            window.open(
                                                NCAA_ELIGIBILITY_CENTER_CREATE_ACCOUNT,
                                                "_blank",
                                                "noopener,noreferrer",
                                            )
                                        }
                                    >
                                        Create account
                                    </button>
                                </div>
                            </div>
                            <div className="ncaa-help-card ncaa-help-card--requirements">
                                <div className="ncaa-help-card-body ncaa-help-card-body--left">
                                    <h3 className="ncaa-help-card-title">NCAA Eligibility Requirements</h3>
                                    <p className="ncaa-help-card-text">
                                        Student-athletes must complete 16 NCAA-approved core courses to qualify for Division I
                                        and II.
                                    </p>
                                    <ul className="ncaa-help-card-list">
                                        <li>10 courses must be completed before senior year</li>
                                        <li>7 of those must be in English, math, or science</li>
                                        <li>
                                            The minimum GPA for NCAA Division I is 2.3, and the minimum GPA for NCAA Division
                                            II is 2.2.
                                        </li>
                                    </ul>
                                    <p className="ncaa-help-card-text ncaa-help-card-text--closing">
                                        Staying on track is critical—POLARIS helps ensure proper course planning and NCAA
                                        eligibility every step of the way.
                                    </p>
                                </div>
                                </div>
                            </div>
                        </div>
                    )}
                </section>

                {/* Bottom panel: worksheet */}
                <section className="ncaa-tracker-panel ncaa-tracker-panel--worksheet" aria-label="NCAA worksheet">
                    <div className="ncaa-worksheet-top-row">
                        <div className="ncaa-tabs" role="tablist">
                            <button
                                type="button"
                                role="tab"
                                aria-selected={tab === "d1"}
                                className={`ncaa-tab ${tab === "d1" ? "ncaa-tab--active" : ""}`}
                                onClick={() => setTab("d1")}
                            >
                                D1 Worksheet
                            </button>
                            <button
                                type="button"
                                role="tab"
                                aria-selected={tab === "d2"}
                                className={`ncaa-tab ${tab === "d2" ? "ncaa-tab--active" : ""}`}
                                onClick={() => setTab("d2")}
                            >
                                D2 Worksheet
                            </button>
                        </div>

                        {!readOnly && (
                            <div className="ncaa-save-top-wrap" aria-live="polite">
                                {saving ? (
                                    <span className="ncaa-saved-hint">Saving…</span>
                                ) : saveError ? (
                                    <span className="ncaa-save-error">{saveError}</span>
                                ) : savedAt ? (
                                    <span className="ncaa-saved-hint">
                                        Saved {new Date(savedAt).toLocaleTimeString()}
                                    </span>
                                ) : null}
                            </div>
                        )}
                    </div>

                    <p className="ncaa-worksheet-intro">{WORKSHEET_INTRO.paragraph}</p>
                    <p className="ncaa-worksheet-scale">
                        <strong>{WORKSHEET_INTRO.scaleBold}</strong>
                    </p>

                {sectionDefs.map((def) => {
                    const sec = division.sections.find((s) => s.id === def.id);
                    if (!sec) return null;
                    const st = sectionTotals(sec);
                    const sectionGpa = st.cr > 0 ? st.qp / st.cr : 0;
                    const reqParen =
                        def.requirement.trim().length > 0
                            ? def.requirement.startsWith("(")
                                ? def.requirement
                                : `(${def.requirement})`
                            : null;
                    return (
                        <section key={def.id} className={`ncaa-section ${tab === "d1" ? "ncaa-section--d1" : "ncaa-section--d2"}`}>
                            <div className="ncaa-table-wrap ncaa-table-wrap--worksheet">
                                <table className="ncaa-table ncaa-table--worksheet">
                                    <colgroup>
                                        <col className="ncaa-col-icy" />
                                        <col className="ncaa-col-title" />
                                        <col className="ncaa-col-credit" />
                                        <col className="ncaa-col-op" />
                                        <col className="ncaa-col-grade" />
                                        <col className="ncaa-col-op" />
                                        <col className="ncaa-col-qp" />
                                    </colgroup>
                                    <thead>
                                        <tr className="ncaa-section-banner-row">
                                            <th colSpan={7} scope="colgroup" className="ncaa-section-banner-cell">
                                                <div className="ncaa-section-banner-inner">
                                                    <div className="ncaa-section-head-text">
                                                        <h2 className="ncaa-section-title">
                                                            <span className="ncaa-section-title-main">{def.title}</span>
                                                            {reqParen ? (
                                                                <span className="ncaa-section-req ncaa-section-req--inline">
                                                                    {reqParen}
                                                                </span>
                                                            ) : null}
                                                        </h2>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="ncaa-add-course"
                                                        onClick={() => addCourse(def.id)}
                                                        disabled={readOnly}
                                                    >
                                                        + Course
                                                    </button>
                                                </div>
                                            </th>
                                        </tr>
                                        <tr className="ncaa-section-colhead-row">
                                            <th className="th-icy">10/7</th>
                                            <th>NCAA-Approved Course Title</th>
                                            <th>Credit</th>
                                            <th className="ncaa-th-op" aria-hidden="true">
                                                ×
                                            </th>
                                            <th>Grade</th>
                                            <th className="ncaa-th-op" aria-hidden="true">
                                                =
                                            </th>
                                            <th className="th-qp-d2">
                                                Quality Points
                                                <span className="ncaa-th-sub">(multiply credit by grade)</span>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sec.rows.map((row) => {
                                            const qualityPoints = rowQualityPoints(row.grade, row.credits);
                                            const creditForFormula = formatFormulaCredit(row.credits);
                                            const gradeForFormula = gradeToPoints(row.grade);
                                            const hasFormula = creditForFormula != null && gradeForFormula != null;

                                            return (
                                            <tr key={row.id}>
                                                <td className="td-icy">
                                                    <label className="ncaa-icy-wrap" htmlFor={`icy-${def.id}-${row.id}`}>
                                                        <input
                                                            id={`icy-${def.id}-${row.id}`}
                                                            type="checkbox"
                                                            className="ncaa-icy-checkbox"
                                                            checked={row.icy}
                                                            onChange={(e) =>
                                                                updateRow(def.id, row.id, { icy: e.target.checked })
                                                            }
                                                            disabled={readOnly}
                                                            aria-label="In core year"
                                                        />
                                                        <span className="ncaa-icy-box" aria-hidden />
                                                    </label>
                                                </td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        className="ncaa-input"
                                                        value={row.title}
                                                        onChange={(e) =>
                                                            updateRow(def.id, row.id, { title: e.target.value })
                                                        }
                                                        disabled={readOnly}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        inputMode="decimal"
                                                        className="ncaa-input ncaa-input--narrow"
                                                        value={row.credits}
                                                        onChange={(e) =>
                                                            updateRow(def.id, row.id, { credits: e.target.value })
                                                        }
                                                        disabled={readOnly}
                                                    />
                                                </td>
                                                <td className="ncaa-td-op" aria-hidden="true">
                                                    ×
                                                </td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        inputMode="numeric"
                                                        className="ncaa-input ncaa-input--narrow"
                                                        value={row.grade}
                                                        onChange={(e) => {
                                                            const next = normalizeGradeInput(e.target.value);
                                                            if (next == null) return;
                                                            updateRow(def.id, row.id, { grade: next });
                                                        }}
                                                        disabled={readOnly}
                                                        maxLength={1}
                                                    />
                                                </td>
                                                <td className="ncaa-td-op" aria-hidden="true">
                                                    =
                                                </td>
                                                <td className="td-qp">
                                                    <span className="ncaa-qp-pill">
                                                        <span className="ncaa-qp-pill-row">
                                                            {hasFormula ? (
                                                                <span className="ncaa-qp-pill-formula">
                                                                    ({creditForFormula} x {gradeForFormula})
                                                                </span>
                                                            ) : (
                                                                <span className="ncaa-qp-pill-formula" />
                                                            )}
                                                            <span className="ncaa-qp-pill-value">{qualityPoints.toFixed(2)}</span>
                                                        </span>
                                                    </span>
                                                </td>
                                            </tr>
                                            );
                                        })}
                                    </tbody>
                                    <tfoot>
                                        <tr>
                                            <td className="ncaa-foot-op" aria-hidden="true" />
                                            <td className="ncaa-foot-label">
                                                <span className="ncaa-foot-pill ncaa-foot-pill--full">
                                                    TOTAL {footerUnitsUpper(def)} UNITS
                                                </span>
                                            </td>
                                            <td className="ncaa-foot-num">
                                                <span className="ncaa-foot-pill ncaa-foot-pill--narrow">{st.cr.toFixed(2)}</span>
                                            </td>
                                            <td className="ncaa-foot-op" aria-hidden="true" />
                                            <td className="ncaa-foot-num">
                                                <span className="ncaa-foot-pill ncaa-foot-pill--narrow">{sectionGpa.toFixed(2)}</span>
                                            </td>
                                            <td className="ncaa-foot-op" aria-hidden="true" />
                                            <td className="ncaa-foot-qp-wide">
                                                <div className="ncaa-qp-pill ncaa-foot-qp-pill ncaa-foot-qp-wide-inner">
                                                    <span className="ncaa-foot-qp-wide-label">TOTAL QUALITY POINTS:</span>
                                                    <span className="ncaa-foot-qp-wide-val">{st.qp.toFixed(2)}</span>
                                                </div>
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </section>
                    );
                })}

                <footer className="ncaa-grand-total" aria-labelledby="ncaa-total-overall-heading">
                    <h2 id="ncaa-total-overall-heading" className="ncaa-grand-title">
                        Total Overall
                    </h2>
                    <div className="ncaa-grand-body">
                        <p className="ncaa-grand-formula">
                            TOTAL QUALITY POINTS FROM EACH SUBJECT AREA / TOTAL CREDITS = CORE-COURSE GPA
                        </p>
                        <div className="ncaa-grand-calcs" aria-label="Core-course GPA calculation">
                            <div className="ncaa-grand-field">
                                <span className="ncaa-grand-field-label">Quality Points</span>
                                <div className="ncaa-grand-field-box">{worksheetTotals.totalQp.toFixed(2)}</div>
                            </div>
                            <span className="ncaa-grand-op" aria-hidden="true">
                                /
                            </span>
                            <div className="ncaa-grand-field">
                                <span className="ncaa-grand-field-label">Credit</span>
                                <div className="ncaa-grand-field-box">{worksheetTotals.totalCr.toFixed(2)}</div>
                            </div>
                            <span className="ncaa-grand-op" aria-hidden="true">
                                =
                            </span>
                            <div className="ncaa-grand-field">
                                <span className="ncaa-grand-field-label">CORE-COURSE GPA</span>
                                <div className="ncaa-grand-field-box">{worksheetTotals.gpa.toFixed(2)}</div>
                            </div>
                        </div>
                    </div>
                </footer>

                <section className="ncaa-disclaimer" aria-labelledby="ncaa-disclaimer-heading">
                    <h3 id="ncaa-disclaimer-heading" className="ncaa-disclaimer-heading">
                        {NCAA_DISCLAIMER.heading}
                    </h3>
                    <p className="ncaa-disclaimer-body">{NCAA_DISCLAIMER.body}</p>
                </section>

                {(saveError || savedAt || saving) && (
                    <div className="ncaa-save-sticky-wrap" aria-live="polite">
                        {saving && <div className="ncaa-saved-hint">Saving…</div>}
                        {!saving && saveError && <div className="ncaa-save-error">{saveError}</div>}
                        {!saving && !saveError && savedAt && (
                            <div className="ncaa-saved-hint">Last saved: {new Date(savedAt).toLocaleString()}</div>
                        )}
                    </div>
                )}
                </section>
            </div>
        </div>
    );
}
